/*
  Full reviews list (sections/nr-product-reviews.liquid).

  The newest five reviews arrive in the page. Sorting and "Show more
  reviews" fetch five at a time from Judge.me's public widget address, the
  one its own widget calls, and only when the shopper asks. It needs no
  token. It isn't documented, so on any failure what is showing stays,
  the button hides and a short note says more couldn't load.

  Review text is the customers' own: everything from Judge.me goes into the
  page as text (textContent), never as markup. Rows are copies of the empty
  row in the section's <template>, so the wording stays in the customiser.

  Long reviews fold after six lines with "Read more"; any that fit unfold.
  Tapping the star rating at the top of the page (our nr_rating block, or a
  Judge.me badge) scrolls here, as it did to Judge.me's own widget.
*/
if (!customElements.get('nr-product-reviews')) {
  const ENDPOINT = 'https://cdn.judge.me/reviews/reviews_for_widget';
  const PER_PAGE = 5;
  const SORTS = {
    recent: ['created_at', 'desc'],
    highest: ['rating', 'desc'],
    lowest: ['rating', 'asc'],
    photos: ['with_pictures', 'desc'],
  };
  // Dates in UK time, as Liquid prints them in the shop's time zone.
  const ZONE = 'Europe/London';
  const DATE_TEXT = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: ZONE });
  const DATE_ISO = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: ZONE });
  const FOLD_FROM = 600; // characters, the same as the snippet

  // Judge.me sends titles and names with HTML entities and the words as
  // simple HTML. An inert document decodes them without running anything.
  const decode = (html) => {
    const doc = new DOMParser().parseFromString(String(html == null ? '' : html), 'text/html');
    return (doc.body && doc.body.textContent) || '';
  };
  // Line breaks become newlines and paragraphs a blank line, as in the
  // plain-text copy the page starts with.
  const toText = (html) =>
    decode(
      String(html == null ? '' : html)
        .replace(/\s*<br\s*\/?>\s*/gi, '\n')
        .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
    ).trim();
  const httpsUrl = (value) => {
    try {
      const url = new URL(String(value));
      return url.protocol === 'https:' ? url.href : '';
    } catch (error) {
      return '';
    }
  };

  customElements.define(
    'nr-product-reviews',
    class NrProductReviews extends HTMLElement {
      connectedCallback() {
        if (this.list) return;
        this.list = this.querySelector('[data-list]');
        this.template = this.querySelector('template[data-row]');
        this.button = this.querySelector('[data-more]');
        this.status = this.querySelector('[data-status]');
        this.select = this.querySelector('[data-sort]');
        if (!this.list || !this.template || !this.button || !this.status || !this.select) return;

        this.sort = 'recent';
        this.total = Number(this.dataset.total) || 0;
        this.pages = Number(this.dataset.pages) || Math.ceil(this.total / PER_PAGE);
        this.first = [...this.list.children];
        this.page = this.first.length ? 1 : 0;
        this.firstTotal = this.total;
        this.firstPages = this.pages;
        this.busy = false;

        this.button.addEventListener('click', () => this.load(this.sort, this.page + 1));
        this.select.addEventListener('change', () => this.changeSort(this.select.value));
        this.list.addEventListener('click', (event) => {
          const fold = event.target.closest('[data-fold]');
          if (fold) this.toggleFold(fold);
        });
        // The star rating at the top of the page (ours or Judge.me's badge).
        document.addEventListener('click', (event) => {
          const link = event.target.closest && event.target.closest('.jdgm-prev-badge, [data-nr-reviews-link]');
          if (!link) return;
          event.preventDefault();
          this.scrollIntoView({ block: 'start' });
        });

        requestAnimationFrame(() => this.unfoldShort(this.first));
      }

      changeSort(sort) {
        if (!SORTS[sort] || this.busy) {
          this.select.value = this.sort;
          return;
        }
        // Most recent's first page is already in the page.
        if (sort === 'recent' && this.first.length) {
          this.sort = 'recent';
          this.page = 1;
          this.total = this.firstTotal;
          this.pages = this.firstPages;
          this.list.replaceChildren(...this.first);
          this.update();
          return;
        }
        this.load(sort, 1);
      }

      async load(sort, page) {
        if (this.busy) return;
        this.busy = true;
        this.setAttribute('aria-busy', 'true');
        this.button.disabled = true;
        try {
          const data = await this.fetchPage(sort, page);
          const replace = page === 1;
          const seen = new Set(replace ? [] : [...this.list.children].map((row) => row.dataset.reviewId));
          const rows = [];
          for (const review of data.reviews) {
            if (!review || typeof review !== 'object') continue;
            const id = String(review.uuid || '');
            if (id && seen.has(id)) continue;
            seen.add(id);
            rows.push(this.buildRow(review));
          }
          if (replace) this.list.replaceChildren(...rows);
          else this.list.append(...rows);

          this.sort = sort;
          this.page = Number(data.pagination.current_page) || page;
          this.pages = Number(data.pagination.total_pages) || 0;
          if (sort !== 'photos' && Number(data.number_of_reviews) > 0) this.total = Number(data.number_of_reviews);
          this.update();
          this.unfoldShort(rows);
          // After "Show more reviews", move to the first new review.
          if (!replace && rows[0]) rows[0].querySelector('article').focus();
        } catch (error) {
          this.fail();
        } finally {
          this.busy = false;
          this.removeAttribute('aria-busy');
          this.button.disabled = false;
        }
      }

      async fetchPage(sort, page) {
        const [sortBy, sortDir] = SORTS[sort];
        const params = new URLSearchParams({
          shop_domain: this.dataset.shop,
          platform: 'shopify',
          product_id: this.dataset.productId,
          page: String(page),
          per_page: String(PER_PAGE),
          sort_by: sortBy,
          sort_dir: sortDir,
        });
        // Judge.me's widget adds the data's timestamp so its CDN serves pages
        // that match the copy in the page.
        if (this.dataset.stamp) params.set('ts', this.dataset.stamp);

        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 10000);
        try {
          const response = await fetch(`${ENDPOINT}?${params}`, {
            credentials: 'omit',
            signal: controller.signal,
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) throw new Error(`Judge.me ${response.status}`);
          const data = await response.json();
          if (!data || !Array.isArray(data.reviews) || !data.pagination) throw new Error('Unexpected reply');
          return data;
        } finally {
          clearTimeout(timer);
        }
      }

      buildRow(review) {
        const row = this.template.content.firstElementChild.cloneNode(true);
        const id = String(review.uuid || Math.random().toString(36).slice(2)).replace(/[^\w-]/g, '');
        row.dataset.reviewId = String(review.uuid || '');

        row.querySelector('[data-name]').textContent = decode(review.reviewer_name).trim();
        if (review.verified_buyer !== true) row.querySelector('[data-verified]')?.remove();

        const rating = Math.min(5, Math.max(0, Math.round(Number(review.rating) || 0)));
        const stars = row.querySelector('.nr-stars');
        if (stars) {
          stars.setAttribute('aria-label', (stars.getAttribute('aria-label') || '').replace(/^[\d.]+/, String(rating)));
          stars.querySelectorAll('.nr-stars__star').forEach((star, index) => {
            star.style.setProperty('--fill', index < rating ? '100%' : '0%');
          });
        }

        const time = row.querySelector('[data-date]');
        const date = new Date(review.created_at);
        if (Number.isNaN(date.getTime())) time.remove();
        else {
          time.textContent = DATE_TEXT.format(date);
          time.setAttribute('datetime', DATE_ISO.format(date));
        }

        const title = decode(review.title).trim();
        const titleLine = row.querySelector('[data-title]');
        if (title && titleLine) titleLine.textContent = title;
        else titleLine?.remove();

        const body = row.querySelector('[data-body]');
        const words = toText(review.body_html) || String(review.body || '').trim();
        body.textContent = words;
        body.id = `${this.dataset.idPrefix || 'review'}-${id}`;
        const fold = row.querySelector('[data-fold]');
        if (words.length > FOLD_FROM && fold) fold.setAttribute('aria-controls', body.id);
        else {
          body.classList.remove('nr-product-reviews__body--fold');
          fold?.remove();
        }

        const photos = row.querySelector('[data-photos]');
        if (photos) {
          const sample = photos.firstElementChild;
          const items = [];
          for (const picture of Array.isArray(review.pictures_urls) ? review.pictures_urls : []) {
            const small = httpsUrl(picture && (picture.small || picture.compact));
            if (!small || !sample) continue;
            const item = sample.cloneNode(true);
            item.querySelector('[data-photo]').href = httpsUrl(picture.huge || picture.original) || small;
            item.querySelector('img').src = small;
            items.push(item);
          }
          if (items.length) photos.replaceChildren(...items);
          else photos.remove();
        }

        const reply = row.querySelector('[data-reply]');
        const replyText = toText(review.reply_content);
        if (reply && replyText) reply.querySelector('[data-reply-text]').textContent = replyText;
        else reply?.remove();

        return row;
      }

      // Rows whose words fit in six lines lose the fold and its button.
      unfoldShort(rows) {
        rows.forEach((row) => {
          const body = row.querySelector('.nr-product-reviews__body--fold');
          if (!body || body.scrollHeight > body.clientHeight + 2) return;
          body.classList.remove('nr-product-reviews__body--fold');
          row.querySelector('[data-fold]')?.remove();
        });
      }

      toggleFold(button) {
        const body = document.getElementById(button.getAttribute('aria-controls'));
        if (!body) return;
        const open = button.getAttribute('aria-expanded') !== 'true';
        body.classList.toggle('is-open', open);
        button.setAttribute('aria-expanded', String(open));
        button.textContent = open ? this.dataset.readLess : this.dataset.readMore;
      }

      update() {
        const shown = this.list.children.length;
        const more = this.page < this.pages;
        this.button.hidden = !more;
        let text;
        if (this.sort === 'photos') text = shown ? this.dataset.showingPhotos : this.dataset.noPhotos;
        else text = this.dataset.showing;
        this.status.textContent = (text || '').replace('[shown]', shown).replace('[count]', Math.max(this.total, shown));
      }

      fail() {
        // Keep what is showing, and the sort that matches it.
        this.select.value = this.sort;
        this.button.hidden = true;
        this.status.textContent = this.dataset.error || '';
      }
    }
  );
}
